// src/components/ladder/portal/LadderMemberGate.tsx
// Spec §3: member routes need a signed-in, admitted player. Signed out → /login with the return
// location in router state (the pattern Home/Login use). Not admitted → one card.
import type { ReactNode } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLadderMe } from "@/hooks/useLadderMe";
import { fetchLadderBySlug } from "@/lib/services/ladders";
import { NOT_REGISTERED_BODY, NOT_REGISTERED_TITLE, REGISTER, SEE_STANDINGS } from "@/lib/ladder-portal-copy";
import { InstallGate } from "@/components/ladder/portal/InstallGate";

const Spinner = () => (
  <div className="flex min-h-[50vh] items-center justify-center bg-background"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
);

export function LadderMemberGate({ children }: { children: ReactNode }) {
  const { slug = "" } = useParams<{ slug: string }>();
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const ladderQuery = useQuery({ queryKey: ["ladder-by-slug", slug], queryFn: () => fetchLadderBySlug(slug), enabled: !!slug });
  const me = useLadderMe(user && ladderQuery.data ? ladderQuery.data.id : undefined);

  if (isLoading) return <Spinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (ladderQuery.isLoading) return <Spinner />;
  if (ladderQuery.isError) return <p className="p-6 text-center text-sm text-muted-foreground">Failed to load. Check your connection and try again.</p>;
  if (!ladderQuery.data) return <p className="p-6 text-center text-sm text-muted-foreground">Ladder not found.</p>;
  if (me.isLoading) return <Spinner />;
  if (me.isError) {
    return (
      <Card className="mx-auto mt-6 flex max-w-sm items-center justify-between gap-3 p-3">
        <span className="text-sm">Couldn't check your registration</span>
        <Button size="sm" variant="outline" className="min-h-11 px-3 text-xs" onClick={() => me.refetch()}>Retry</Button>
      </Card>
    );
  }
  if (!me.data?.is_admitted) {
    return (
      <div className="mx-auto max-w-2xl p-3 md:px-6 md:py-4">
        <Card className="space-y-3 p-3">
          <h1 className="font-display text-lg font-bold tracking-tight">{NOT_REGISTERED_TITLE}</h1>
          <p className="text-sm text-muted-foreground">{NOT_REGISTERED_BODY}</p>
          <div className="flex gap-2">
            <Button asChild variant="outline" className="min-h-11 flex-1"><Link to={`/ladders/${slug}`}>{SEE_STANDINGS}</Link></Button>
            <Button asChild className="min-h-11 flex-1"><Link to={`/ladders/${slug}/register`}>{REGISTER}</Link></Button>
          </div>
        </Card>
      </div>
    );
  }
  return <InstallGate>{children}</InstallGate>;
}
